import type { AxiosInstance } from "axios";
import type { APIResponse } from "@/core/types/api";
import type {
  AddToCartDto,
  CheckoutDto,
  CustomerCart,
  CustomerOrder,
  CustomerProduct,
  CustomerShopSummary,
  MarketplaceResponse,
  UpdateCartItemDto,
} from "../types/customer-shop";
import type { MarketplaceQueryFilters } from "../types/marketplace-filters";

export function customerShopService(api: AxiosInstance) {
  return {
    getMarketplace(
      params?: MarketplaceQueryFilters,
    ): Promise<APIResponse<MarketplaceResponse>> {
      return api.get("/shop", { params }).then((res) => res.data);
    },
    searchProducts(
      query: string,
      limit?: number,
    ): Promise<APIResponse<CustomerProduct[]>> {
      return api
        .get("/shop/products/search", { params: { q: query, limit } })
        .then((res) => res.data);
    },
    getProduct(productId: string): Promise<APIResponse<CustomerProduct>> {
      return api.get(`/shop/products/${productId}`).then((res) => res.data);
    },
    getShop(
      shopId: string,
      params?: { category?: string },
    ): Promise<
      APIResponse<{
        shop: CustomerShopSummary;
        products: MarketplaceResponse["products"];
      }>
    > {
      return api.get(`/shop/${shopId}`, { params }).then((res) => res.data);
    },
    getCart(): Promise<APIResponse<CustomerCart | null>> {
      return api.get("/shop/cart").then((res) => res.data);
    },
    addToCart(data: AddToCartDto): Promise<APIResponse<CustomerCart>> {
      return api.post("/shop/cart", data).then((res) => res.data);
    },
    syncCart(data: {
      items: AddToCartDto[];
    }): Promise<APIResponse<CustomerCart>> {
      return api.put("/shop/cart", data).then((res) => res.data);
    },
    updateCartItem(
      productId: string,
      data: UpdateCartItemDto,
    ): Promise<APIResponse<CustomerCart>> {
      return api
        .put(`/shop/cart/items/${productId}`, data)
        .then((res) => res.data);
    },
    removeCartItem(productId: string): Promise<APIResponse<CustomerCart>> {
      return api
        .delete(`/shop/cart/items/${productId}`)
        .then((res) => res.data);
    },
    clearCart(): Promise<APIResponse<{ cleared: true }>> {
      return api.delete("/shop/cart").then((res) => res.data);
    },
    checkout(data: CheckoutDto): Promise<APIResponse<CustomerOrder>> {
      return api.post("/shop/orders", data).then((res) => res.data);
    },
    getOrders(): Promise<APIResponse<CustomerOrder[]>> {
      return api.get("/shop/orders").then((res) => res.data);
    },
    getOrder(orderId: string): Promise<APIResponse<CustomerOrder>> {
      return api.get(`/shop/orders/${orderId}`).then((res) => res.data);
    },
  };
}
